import { Manager, FileDto, DEFAULT_TIMEZONE } from '../api/data-model';
import { ForeignNational } from '../api/foreign-national/foreign-national';
import { User } from '../api/user/user';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

export function getFullName(item: ForeignNational | User): string {
  const temp = item as any;
  if ('surname' in temp) {
    // User
    if (temp.name || temp.surname) {
      return temp.name + (temp.surname ? ' ' + temp.surname : '');
    } else {
      return temp.email;
    }
  } else {
    // ForeignNational and others
    return temp.firstName + (temp.middleName ? ' ' + temp.middleName : '') + ' ' + temp.lastName;
  }
}

// export function getFileIcon(fileName: string): string {
//   if (!fileName) return 'pi pi-file';
//   const parts = fileName.split('.');
//   const extension = parts.length > 1 ? parts.pop()?.toLowerCase() ?? null : null;

//   if (!extension) {
//     return 'pi pi-folder';
//   }

//   switch (extension) {
//     case 'pdf':
//       return 'pi pi-file-pdf';
//     case 'doc':
//     case 'docx':
//       return 'pi pi-file-word';
//     case 'xls':
//     case 'xlsx':
//       return 'pi pi-file-excel';
//     default:
//       return 'pi pi-file';
//   }
// }

export function getPrimaryPerson(people: Manager[] | undefined | null): string {

  if (!people || people.length === 0) {
    return '';
  }

  const primaryPerson = people.find((person) => person.isPrimary);
  return primaryPerson ? primaryPerson.fullName : people[0].fullName;
}

export function dateString2Date(value: any): Date | undefined {
  if (!value) return undefined;

  const date = dayjs(value);
  return date.isValid() ? date.toDate() : undefined;
}

export function openFile(file: FileDto) {
  const a = document.createElement('a');
  a.href = file.url;
  a.download = file.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function openFile2(file: FileDto) {
  const byteCharacters = atob(file.url);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: 'application/octet-stream' });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

export function openUrl(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function toPTDateString(date: Date | string | null | undefined): string {
  if (!date) return '';

  dayjs.extend(utc);
  dayjs.extend(timezone);

  // If it's a string and doesn't end with Z, append Z to treat as UTC
  const dateStr = typeof date === 'string' && !hasTimeZone(date) ? `${date}Z` : date;

  const ptDate = dayjs(dateStr).tz(DEFAULT_TIMEZONE);

  return ptDate.format('MM/DD/YYYY');
}
export function toPTDateStringYYYYMMDD(date: Date | string | null | undefined): string {
  if (!date) return '';

  dayjs.extend(utc);
  dayjs.extend(timezone);

  // If it's a string and doesn't end with Z, append Z to treat as UTC
  const dateStr = typeof date === 'string' && !hasTimeZone(date) ? `${date}Z` : date;

  const ptDate = dayjs(dateStr).tz(DEFAULT_TIMEZONE);

  return ptDate.format('YYYY/MM/DD');
}

function hasTimeZone(input: string): boolean {
  const timeZoneRegex = /(Z|[+-](?:2[0-3]|[01]\d):?[0-5]\d)$/;
  return timeZoneRegex.test(input);
}

export function downloadCsv(fileName: string, blob: Blob): void {
  const a = document.createElement('a');
  const objectUrl = URL.createObjectURL(blob);
  a.href = objectUrl;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

export function createEnumOptions<T extends { [key: string]: string | number }>(enumObj: T) {
  return Object.keys(enumObj)
    .filter((key) => Number.isNaN(Number(key)))
    .map((label) => ({
      value: enumObj[label as keyof T],
      label,
    }));
}

export function getReplacedFilesIntroMessage(
  fileCount: number,
): string {
  const filesLabel = fileCount === 1 ? "file" : "files";
  const pronoun = fileCount === 1 ? "it" : "they";
  return `The following ${filesLabel} already existed, so ${pronoun} got replaced.`;
}

/** Display name for uploaded SharePoint docs titled as "{docTitle} - {originalFileName}". */
export function getUploadedDocumentDisplayName(
  name?: string | null,
  webUrl?: string | null
): string {
  const raw = (name?.trim() || extractFilenameFromUrl(webUrl ?? "")).trim();
  if (!raw) return "Unknown file";
  const sep = " - ";
  const idx = raw.lastIndexOf(sep);
  if (idx >= 0 && idx < raw.length - sep.length) {
    return raw.substring(idx + sep.length).trim();
  }
  return raw;
}

export function extractFilenameFromUrl(url: string): string {
  if (!url) return '';

  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filenameWithParams = pathname.split('/').pop() || '';

    const filename = filenameWithParams.split('?')[0];

    const filenameWithoutHash = filename.split('#')[0];

    return decodeURIComponent(filenameWithoutHash);
  } catch (e) {
    console.warn('extractFilenameFromUrl failed; continuing with fallback', e);
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1] || '';
    return decodeURIComponent(lastPart.split('?')[0].split('#')[0]);
  }
}

  export function extractFileNameWithoutExtension(name: string): string {
    if (!name) return '';

    const lastDotIndex = name.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return name;
    }

    return name.substring(0, lastDotIndex);
  }

  export function isFolderByUrl(url: string): boolean {
    if (!url) return true;

    const urlObj = new URL(url);
    const segments = urlObj.pathname.split('/').filter(Boolean);
    const lastSegment = decodeURIComponent(segments[segments.length - 1]);

    return !lastSegment.includes('.');
  }
